/*!
 * Find the differences between two objects and push to a new object
 * (c) 2019 Chris Ferdinandi & Jascha Brinkmann, MIT License, https://gomakethings.com & https://twitter.com/jaschaio
 * https://vanillajstoolkit.com/helpers/diff/
 * @param  {Object} obj1 The original object
 * @param  {Object} obj2 The object to compare against it
 * @return {Object}      An object of differences between the two
 */
export const getDiff = (obj1: any, obj2: any) => {
    // Make sure an object to compare is provided
    if (!obj2 || Object.prototype.toString.call(obj2) !== "[object Object]") {
        return obj1;
    }

    //
    // Variables
    //

    const diffs = {};
    let key;

    //
    // Methods
    //

    /**
     * Check if two arrays are equal
     * @param  {Array}   arr1 The first array
     * @param  {Array}   arr2 The second array
     * @return {Boolean}      If true, both arrays are equal
     */
    const arraysMatch = (arr1: any, arr2: any) => {
        // Check if the arrays are the same length
        if (arr1.length !== arr2.length) return false;

        // Check if all items exist and are in the same order
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) return false;
        }

        // Otherwise, return true
        return true;
    };

    /**
     * Compare two items and push non-matches to object
     * @param  {*}      item1 The first item
     * @param  {*}      item2 The second item
     * @param  {String} compareKey   The key in our object
     */
    const compare = (item1: any, item2: any, compareKey: any) => {
        // Get the object type
        const type1 = Object.prototype.toString.call(item1);
        const type2 = Object.prototype.toString.call(item2);

        // If type2 is undefined it has been removed, but type1 has to be !undefined in order to detect a change
        if (type2 === "[object Undefined]" && type1 !== "[object Undefined]") {
            diffs[compareKey] = null;
            return;
        }

        // If items are different types
        if (type1 !== type2) {
            diffs[compareKey] = item2;
            return;
        }

        // If an object, compare recursively
        if (type1 === "[object Object]") {
            const objDiff = getDiff(item1, item2);
            if (Object.keys(objDiff).length >= 1) {
                diffs[compareKey] = objDiff;
            }
            return;
        }

        // If an array, compare
        if (type1 === "[object Array]") {
            if (!arraysMatch(item1, item2)) {
                diffs[compareKey] = item2;
            }
            return;
        }

        // Else if it's a function, convert to a string and compare
        // Otherwise, just compare
        if (type1 === "[object Function]") {
            if (item1.toString() !== item2.toString()) {
                diffs[compareKey] = item2;
            }
        } else {
            if (item1 !== item2) {
                diffs[compareKey] = item2;
            }
        }
    };

    //
    // Compare our objects
    //

    // Loop through the first object
    for (key in obj1) {
        if (obj1.hasOwnProperty(key)) {
            compare(obj1[key], obj2[key], key);
        }
    }

    // Loop through the second object and find missing items
    for (key in obj2) {
        if (obj2.hasOwnProperty(key)) {
            if (!obj1[key] && obj1[key] !== obj2[key]) {
                diffs[key] = obj2[key];
            }
        }
    }

    // Return the object of differences
    return diffs;
};

export const convertDiffsToTouched = <T extends object = any>(
    obj: T,
    obj2: T
) => {
    const diffs = getDiff(obj, obj2);
    if (!diffs) return;

    return convertToTouched(diffs);
};

const convertToTouched = (obj: any) => {
    const result = {};
    const keys = Object.keys(obj);

    keys.forEach(k => {
        if (obj[k] === null || obj[k] === undefined) return;
        if (obj[k].toString() === "[object Object]") {
            result[k] = convertToTouched(obj[k]);
        } else {
            result[k] = true;
        }
    });
    return result;
};
